import {
  Component,
  ElementRef,
  computed,
  forwardRef,
  input,
  signal,
  viewChild
} from '@angular/core';
import { inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';
import { foldForSearch } from '@core/utils/search-text.utils';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import {
  CustomValueConfig,
  CustomValueStrategy,
  FormBuilderBase
} from '@shared/components/row-edition-form/form-builder/form-builder.model';
import {
  unwrapValue,
  wrapValue
} from '@shared/components/row-edition-form/row-edition-form.utils';

import { HighlightedMatchComponent } from './highlighted-match.component';

/**
 * Multi-value input with autocompletion: selections become `.gx-token` pills in a
 * `.gx-tags` box, suggestions drop into a `.g-menu` panel with the typed slice
 * highlighted.
 *
 * The panel is rendered by the dropdown singleton, which owns click-outside and
 * Escape. That is why there is no blur timeout here — one would race the
 * singleton's own outside-click walk and close the panel before a click on a
 * suggestion could land.
 */
@Component({
  selector: 'app-multi-autocomplete',
  imports: [DropdownComponent, TranslatePipe, HighlightedMatchComponent],
  templateUrl: './multi-autocomplete.component.html',
  styles: `
    :host {
      display: block;
      width: 100%;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiAutocompleteComponent),
      multi: true
    }
  ]
})
export class MultiAutocompleteComponent implements ControlValueAccessor {
  id = input<FormBuilderBase['id']>();
  options = input<readonly string[] | undefined>([]);
  allowCustomValues = input<boolean | undefined>(true);
  placeholder = input<string>();
  separators = input<readonly string[]>([',', ';']);
  customValueConfig = input<CustomValueConfig | undefined>(undefined);
  wrapperProperty = input<string | undefined>(undefined);
  disabled = input<boolean>(false);

  protected readonly ICONS = ICONS;
  protected readonly wrapper = viewChild.required<ElementRef<HTMLElement>>('wrapperEl');

  protected readonly selectedItems = signal<string[]>([]);
  protected readonly searchText = signal('');
  protected readonly highlightedIndex = signal(-1);
  protected readonly isOpen = signal(false);
  protected readonly wrapperWidth = signal(0);
  private readonly isDisabledByForm = signal(false);

  private readonly translate = inject(TranslateService);
  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  protected readonly isEffectivelyDisabled = computed(
    () => this.disabled() || this.isDisabledByForm()
  );

  /**
   * Suggestions are everything unselected that contains the search text, plus —
   * when custom values are allowed and what was typed is genuinely new — a
   * trailing "create" row carrying the raw text.
   */
  protected readonly filteredOptions = computed<string[]>(() => {
    const search = foldForSearch(this.searchText()).trim();
    const options = this.options() ?? [];
    const selected = this.selectedItems();

    const matching = options.filter(
      (option) => !selected.includes(option) && foldForSearch(option).includes(search)
    );

    if (!this.allowCustomValues() || search.length === 0) {
      return matching;
    }

    const known = options.some((option) => option.toLowerCase() === search);
    const taken = selected.some((item) => item.toLowerCase() === search);
    return known || taken ? matching : [...matching, this.searchText().trim()];
  });

  protected isCustomValue(option: string): boolean {
    return !(this.options() ?? []).some(
      (existing) => existing.toLowerCase() === option.toLowerCase()
    );
  }

  writeValue(value: (string | Record<string, string>)[]): void {
    const unwrapped = unwrapValue(value, this.wrapperProperty()) as
      | (string | Record<string, string>)[]
      | null
      | undefined;

    if (!unwrapped) {
      this.setSelectedItems([]);
      return;
    }

    const config = this.customValueConfig();
    if (
      config?.strategy === CustomValueStrategy.WRAP_INDIVIDUAL &&
      config.wrapperProperty
    ) {
      const property = config.wrapperProperty;
      this.setSelectedItems(
        unwrapped.map((item) =>
          typeof item === 'object' && item !== null && property in item
            ? item[property]
            : (item as string)
        )
      );
      return;
    }

    this.setSelectedItems(unwrapped as string[]);
  }

  /**
   * Writes only on a real change. Callers commonly hand `ngModel` a freshly built
   * array (`Array.isArray(v) ? v : []`), and an unconditional `set()` would dirty
   * the signal every change-detection pass — NgModel then writes back, and the two
   * loop until the app freezes.
   */
  private setSelectedItems(items: string[]): void {
    const current = this.selectedItems();
    if (
      current.length === items.length &&
      current.every((item, index) => item === items[index])
    ) {
      return;
    }
    this.selectedItems.set(items);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = (value: unknown) => fn(wrapValue(value, this.wrapperProperty()));
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabledByForm.set(isDisabled);
  }

  protected onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const lastChar = value.slice(-1);

    // A separator keystroke commits what precedes it rather than being typed.
    if (this.separators().includes(lastChar) && this.allowCustomValues()) {
      const custom = value.slice(0, -1).trim();
      if (custom.length > 0 && !this.selectedItems().includes(custom)) {
        this.selectOption(custom);
      }
      input.value = '';
      this.searchText.set('');
      return;
    }

    this.searchText.set(value);
    this.refreshPanel();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    const options = this.filteredOptions();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (options.length > 0) {
          this.highlightedIndex.update((index) => (index + 1) % options.length);
        }
        return;

      case 'ArrowUp':
        event.preventDefault();
        if (options.length > 0) {
          this.highlightedIndex.update((index) =>
            index <= 0 ? options.length - 1 : index - 1
          );
        }
        return;

      case 'Enter': {
        event.preventDefault();
        const index = this.highlightedIndex();
        if (index >= 0 && index < options.length) {
          this.selectOption(options[index]);
        } else if (options.length > 0) {
          this.selectOption(options[0]);
        } else if (this.allowCustomValues() && this.searchText().trim().length > 0) {
          this.selectOption(this.searchText().trim());
        }
        return;
      }

      case 'Tab':
        // No preventDefault: focus must still move on.
        if (this.searchText().trim().length > 0) {
          if (options.length > 0) {
            this.selectOption(options[0]);
          } else if (this.allowCustomValues()) {
            this.selectOption(this.searchText().trim());
          }
        }
        this.close();
        return;

      case 'Backspace': {
        const items = this.selectedItems();
        if (this.searchText().length === 0 && items.length > 0) {
          this.removeItem(items[items.length - 1]);
        }
        return;
      }

      case 'Escape':
        this.close();
        this.onTouched();
        return;
    }
  }

  protected onInputFocus(): void {
    this.refreshPanel();
  }

  protected selectOption(option: string): void {
    if (this.selectedItems().includes(option)) return;

    this.selectedItems.update((items) => [...items, option]);
    this.searchText.set('');
    this.highlightedIndex.set(-1);
    this.notifyChanges();
    this.refreshPanel();
  }

  protected removeItem(item: string): void {
    if (this.isEffectivelyDisabled()) return;
    this.selectedItems.update((items) => items.filter((selected) => selected !== item));
    this.notifyChanges();
  }

  protected close = (): void => {
    this.isOpen.set(false);
    this.highlightedIndex.set(-1);
  };

  protected readonly createLabel = computed(() => this.translate.instant('common.add'));

  private refreshPanel(): void {
    if (this.isEffectivelyDisabled()) return;

    const hasSuggestions = this.filteredOptions().length > 0;
    if (hasSuggestions && !this.isOpen()) {
      this.wrapperWidth.set(this.wrapper().nativeElement.offsetWidth);
    }
    this.isOpen.set(hasSuggestions);
    this.highlightedIndex.set(hasSuggestions ? 0 : -1);
  }

  private notifyChanges(): void {
    const config = this.customValueConfig();
    const items = this.selectedItems();

    if (
      config?.strategy === CustomValueStrategy.WRAP_INDIVIDUAL &&
      config.wrapperProperty
    ) {
      const property = config.wrapperProperty;
      this.onChange(
        items.map((item) => (this.isCustomValue(item) ? { [property]: item } : item))
      );
      return;
    }

    this.onChange(items);
  }
}
